import { useCallback, useEffect, useState } from "react";
import { Activity, AlertTriangle, BarChart3, Clock, Euro, ShieldCheck, TrendingUp } from "lucide-react";
import { getOperatorAnalytics, setComplianceContext } from "../../services/compliance-api";
import { useComplianceAuth } from "../../context/ComplianceAuthContext";
import type { OperatorAnalyticsResponse } from "../../types/compliance";

function formatEuro(value: number): string {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(value);
}

function StatCard({
  label,
  value,
  helper,
  icon: Icon,
}: {
  label: string;
  value: string | number;
  helper: string;
  icon: typeof Euro;
}) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {label}
        </span>
        <Icon size={17} className="text-blue-600" />
      </div>
      <p className="text-2xl font-bold text-gray-900">{value}</p>
      <p className="mt-1 text-xs text-gray-500">{helper}</p>
    </div>
  );
}

export default function OperatorAnalyticsDashboard() {
  const { credentials } = useComplianceAuth();
  const [analytics, setAnalytics] = useState<OperatorAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setComplianceContext(
        credentials?.tenantId ?? "default",
        credentials?.role ?? "COMPLIANCE_ADMIN",
      );
      setAnalytics(await getOperatorAnalytics());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load operator analytics.");
    } finally {
      setLoading(false);
    }
  }, [credentials?.tenantId, credentials?.role]);

  useEffect(() => {
    void loadAnalytics();
  }, [loadAnalytics]);

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-blue-600" />
        <span className="ml-3 text-gray-600">Loading operator analytics...</span>
      </div>
    );
  }

  if (error || !analytics) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="max-w-xl rounded-lg border border-red-200 bg-red-50 px-6 py-4 text-red-700">
          <h3 className="mb-2 font-bold">Analytics Error</h3>
          <p className="text-sm">{error ?? "Analytics data is unavailable."}</p>
        </div>
      </div>
    );
  }

  const severityEntries = Object.entries(analytics.risk_breakdown.severity);
  const throughputEntries = Object.entries(analytics.case_throughput);

  return (
    <div className="h-full overflow-y-auto p-6">
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
            <BarChart3 className="text-blue-600" size={28} />
            Operator Analytics
          </h1>
          <p className="mt-1 text-sm text-gray-500">
            ROI, throughput, risk exposure, and automation value for production logistics operations.
          </p>
        </div>
        <button
          type="button"
          onClick={() => void loadAnalytics()}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Refresh
        </button>
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Prevented Exposure"
          value={formatEuro(analytics.prevented_exposure_eur)}
          helper="Estimated exposure prevented from high-risk signals."
          icon={Euro}
        />
        <StatCard
          label="Hours Saved"
          value={analytics.estimated_hours_saved.toFixed(1)}
          helper="Estimated operator review time saved."
          icon={Clock}
        />
        <StatCard
          label="Delay Cost Avoided"
          value={formatEuro(analytics.estimated_delay_cost_avoided_eur)}
          helper="Cross-document issues caught before port/customs delays."
          icon={TrendingUp}
        />
        <StatCard
          label="Automated Checks"
          value={analytics.automated_checks}
          helper={`${analytics.registry_checks} registry checks, ${analytics.sanctions_matches} sanctions matches.`}
          icon={ShieldCheck}
        />
      </div>

      <div className="mb-6 grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <StatCard label="Cases" value={analytics.total_cases} helper={`${analytics.open_cases} open, ${analytics.closed_cases} closed.`} icon={Activity} />
        <StatCard label="Documents" value={analytics.total_documents} helper={`${analytics.latest_documents} latest, ${analytics.superseded_documents} superseded.`} icon={BarChart3} />
        <StatCard label="Risks" value={analytics.total_risks} helper={`${analytics.high_risks} high-severity risks.`} icon={AlertTriangle} />
        <StatCard label="Exceptions" value={analytics.exception_cases} helper="Shipment cases currently in exception state." icon={AlertTriangle} />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold text-gray-900">Top Risk Categories</h2>
          {analytics.top_risk_categories.length === 0 ? (
            <p className="text-sm text-gray-500">No risks captured yet.</p>
          ) : (
            <div className="space-y-3">
              {analytics.top_risk_categories.map((item) => (
                <div key={item.category}>
                  <div className="mb-1 flex items-center justify-between text-xs">
                    <span className="font-medium text-gray-700">{item.category.replace(/_/g, " ")}</span>
                    <span className="font-semibold text-gray-900">{item.count}</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100">
                    <div
                      className="h-2 rounded-full bg-blue-600"
                      style={{ width: `${Math.min(100, item.count * 20)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold text-gray-900">Risk Severity</h2>
          <div className="space-y-3">
            {severityEntries.map(([severity, count]) => (
              <div key={severity} className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 text-sm">
                <span className="capitalize text-gray-600">{severity}</span>
                <span className="font-bold text-gray-900">{count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-sm font-semibold text-gray-900">Case Throughput</h2>
          {throughputEntries.length === 0 ? (
            <p className="text-sm text-gray-500">No shipment cases yet.</p>
          ) : (
            <div className="space-y-3">
              {throughputEntries.map(([status, count]) => (
                <div key={status} className="flex items-center justify-between rounded-md bg-gray-50 px-3 py-2 text-sm">
                  <span className="capitalize text-gray-600">{status.replace(/_/g, " ")}</span>
                  <span className="font-bold text-gray-900">{count}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <p className="mt-6 text-xs text-gray-400">
        Generated {new Date(analytics.generated_at).toLocaleString()} for tenant {analytics.tenant_id}.
      </p>
    </div>
  );
}
